/** Google Tag Manager container ID, e.g. `GTM-XXXXXXX`. */
const GTM_ID = /^GTM-[A-Z0-9]+$/i
/** Google Analytics 4 measurement ID, e.g. `G-XXXXXXXXXX`. */
const GA4_ID = /^G-[A-Z0-9]+$/i

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

export type GoogleTagsOptions = {
  gtmContainerId: string
  gaMeasurementId: string
}

const isValidGtm = (id: string) => GTM_ID.test(id.trim())
const isValidGa4 = (id: string) => GA4_ID.test(id.trim())

/**
 * Loads GTM when `gtmContainerId` is set.
 * Direct GA4 gtag is injected at build time in `index.html` (Vite `transformIndexHtml`) so it works
 * with Cloudflare Workers that only serve static assets (no runtime Worker env for `VITE_*`).
 */
export const initGoogleTags = ({ gtmContainerId, gaMeasurementId }: GoogleTagsOptions) => {
  const gtm = gtmContainerId.trim()
  const ga = gaMeasurementId.trim()

  if (gtm) {
    if (!isValidGtm(gtm)) {
      if (import.meta.env.DEV) {
        console.warn('[analytics] Ignoring invalid VITE_GTM_CONTAINER_ID')
      }
      return
    }
    if (document.getElementById('gtm-script')) return

    const script = document.createElement('script')
    script.id = 'gtm-script'
    script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtm}');`
    document.head.appendChild(script)

    const noscript = document.createElement('noscript')
    noscript.id = 'gtm-noscript'
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtm)}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    noscript.appendChild(iframe)
    document.body.insertBefore(noscript, document.body.firstChild)
    return
  }

  if (ga && import.meta.env.DEV && !isValidGa4(ga)) {
    console.warn('[analytics] Ignoring invalid VITE_GA_MEASUREMENT_ID')
  }
}

/**
 * Sends a GA4 `page_view` after client-side navigation (React Router).
 * The initial load is covered by the gtag snippet in built `index.html`.
 * No-op when GTM is used (configure History Change in GTM instead).
 */
export const sendGa4SpaPageView = (
  measurementId: string,
  pagePath: string,
  pageLocation: string = typeof window !== 'undefined' ? window.location.href : '',
  pageTitle: string = typeof document !== 'undefined' ? document.title : '',
) => {
  const id = measurementId.trim()
  if (!isValidGa4(id)) return
  const gtag = window.gtag
  if (typeof gtag !== 'function') return
  gtag('event', 'page_view', {
    page_title: pageTitle,
    page_location: pageLocation,
    page_path: pagePath,
  })
}
